import type { Location, History } from 'history'
import type { ParsedQuery } from 'query-string'

import type { IdentityProviderType } from '@b-stock/account-api-client'
import type { ManagedAccessToken } from '@b-stock/fe-core/bstock-react'

import type { AsyncReducerError } from '@helpers/makeReducer'

import type { accessTokenCheckSaga, authCodeSaga } from './saga'
import types from './types'

export const pending = () => ({
  type: types.AUTH_PENDING,
})

export const fill = () => ({
  type: types.AUTH_FULFILLED,
})

export const reject = (error: AsyncReducerError) => ({
  type: types.AUTH_REJECTED,
  error,
})

export const loaded = () => ({
  type: types.AUTH_LOADED,
})

export const migrationPending = () => ({
  type: types.AUTH_MIGRATION_PENDING,
})

export const accessToken = (
  identityProvider: IdentityProviderType,
  payload: ParsedQuery,
  history: History,
  managedAccessToken: ManagedAccessToken,
  /** PKCE verifier; absent for legacy logins. */
  codeVerifier?: string
) =>
  ({
    type: types.ACCESS_TOKEN,
    payload,
    identityProvider,
    history,
    managedAccessToken,
    codeVerifier,
  }) satisfies Parameters<typeof authCodeSaga>[0] & {
    type: typeof types.ACCESS_TOKEN
  }

export const accessTokenPending = () => ({
  type: types.ACCESS_TOKEN_PENDING,
})

export const accessTokenFulfilled = () => ({
  type: types.ACCESS_TOKEN_FULFILLED,
})

// The raw caught error, forwarded verbatim. `asyncReducer`'s rejected handler
// digs `error.response.data.errorMessage` / `error.response.status` out of it
// with lodash `get`, so the shape stays deliberately opaque here rather than
// being narrowed to `AsyncReducerError` (which is what the reducer *produces*,
// not what it consumes).
export const accessTokenRejected = (error: unknown) => ({
  type: types.ACCESS_TOKEN_REJECTED,
  error,
})

export type CheckAccessTokenPayload = {
  location?: Location
  tokenOnly: boolean
  redirectToLogin: boolean
  redirectAfterLogin: string
}

export const checkAccessToken = (
  payload: CheckAccessTokenPayload,
  history: History,
  managedAccessToken: ManagedAccessToken
) =>
  ({
    type: types.ACCESS_TOKEN_CHECK,
    payload,
    history,
    managedAccessToken,
  }) satisfies Parameters<typeof accessTokenCheckSaga>[0] & {
    type: typeof types.ACCESS_TOKEN_CHECK
  }
